<template>
  <div class="cookie-consent">
    <transition name="cookie-slide">
      <aside
        v-if="isBannerVisible"
        class="cookie-consent__banner"
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-consent-title"
      >
        <h2 id="cookie-consent-title" class="cookie-consent__title">
          {{ $t('cookies.title') }}
        </h2>
        <p class="cookie-consent__message">
          {{ $t('cookies.message') }}
        </p>
        <p class="cookie-consent__more-info">
          {{ $t('cookies.moreInfo') }}
          <a
            class="cookie-consent__privacy-link"
            href="https://www.mesimedical.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
          >{{ $t('cookies.privacyPolicy') }}</a>.
        </p>

        <button class="cookie-consent__manage" type="button" @click="openPreferences">
          {{ $t('cookies.manage') }}
        </button>

        <div class="cookie-consent__actions">
          <button class="cookie-consent__button cookie-consent__button--decline" type="button" @click="acceptEssential">
            {{ $t('cookies.acceptEssential') }}
          </button>
          <button class="cookie-consent__button cookie-consent__button--accept" type="button" @click="acceptAll">
            {{ $t('cookies.acceptAll') }}
          </button>
        </div>
      </aside>
    </transition>

    <button
      v-if="isSettingsButtonVisible"
      class="cookie-consent__settings"
      type="button"
      :aria-label="$t('cookies.settings')"
      :title="$t('cookies.settings')"
      @click="openPreferences"
    >
      <img
        class="cookie-consent__settings-icon"
        :src="require('@/assets/cookie.svg')"
        alt=""
        aria-hidden="true"
        width="32"
        height="32"
      />
    </button>

    <v-dialog
      v-model="isPreferencesVisible"
      content-class="cookie-preferences-dialog"
      max-width="620"
      scrollable
    >
      <v-card class="cookie-preferences">
        <v-card-title class="cookie-preferences__title">
          {{ $t('cookies.manage') }}
        </v-card-title>
        <v-card-text class="cookie-preferences__content">
          <p>{{ $t('cookies.preferencesIntro') }}</p>

          <div class="cookie-preferences__item">
            <v-switch
              :input-value="true"
              :label="$t('cookies.necessary.title')"
              color="accent"
              disabled
              hide-details
            />
            <p>{{ $t('cookies.necessary.description') }}</p>
          </div>

          <div class="cookie-preferences__item">
            <v-switch
              v-model="optionalCookies"
              :label="$t('cookies.optional.title')"
              color="accent"
              hide-details
            />
            <p>{{ $t('cookies.optional.description') }}</p>
          </div>
        </v-card-text>
        <v-card-actions class="cookie-preferences__actions">
          <button class="cookie-consent__button cookie-consent__button--decline" type="button" @click="closePreferences">
            {{ $t('cookies.cancel') }}
          </button>
          <button class="cookie-consent__button cookie-consent__button--accept" type="button" @click="savePreferences">
            {{ $t('cookies.save') }}
          </button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  deleteOptionalCookies,
  hasOptionalConsent,
  prepareConsentStorage,
  readConsent,
  saveConsent
} from '@/analytics/consent'
import {
  grantMatomoCookieConsent,
  revokeMatomoCookieConsent
} from '@/analytics/matomo'

export default {
  name: 'CookieConsent',
  data () {
    return {
      consent: null,
      isBannerVisible: false,
      isPreferencesVisible: false,
      optionalCookies: false
    }
  },
  computed: {
    isSettingsButtonVisible () {
      return Boolean(this.consent) && !this.isBannerVisible && !this.isPreferencesVisible
    }
  },
  mounted () {
    this.consent = prepareConsentStorage()
    this.optionalCookies = hasOptionalConsent(this.consent)
    this.isBannerVisible = this.consent === null
  },
  methods: {
    openPreferences () {
      this.consent = readConsent()
      this.optionalCookies = hasOptionalConsent(this.consent)
      this.isPreferencesVisible = true
    },
    closePreferences () {
      this.isPreferencesVisible = false
    },
    acceptAll () {
      this.applyConsent(true)
    },
    acceptEssential () {
      this.applyConsent(false)
    },
    savePreferences () {
      this.applyConsent(this.optionalCookies)
    },
    applyConsent (optional) {
      const previousConsent = readConsent()
      const hadOptionalConsent = hasOptionalConsent(previousConsent)
      const gtmScriptLoaded = Boolean(document.querySelector('script[src*="googletagmanager.com/gtm.js"]'))

      this.consent = saveConsent(optional)
      this.optionalCookies = optional
      this.isBannerVisible = false
      this.isPreferencesVisible = false

      if (optional) {
        grantMatomoCookieConsent()
        this.$gtm.enable(true)
        return
      }

      revokeMatomoCookieConsent()
      this.$gtm.enable(false)
      deleteOptionalCookies()

      if (hadOptionalConsent || gtmScriptLoaded) {
        this.reloadPage()
      }
    },
    reloadPage () {
      window.location.reload()
    }
  }
}
</script>

<style lang="scss">
.cookie-consent {
  position: relative;
  z-index: 210;
}

.cookie-consent__banner {
  position: fixed;
  left: 20px;
  bottom: 20px;
  z-index: 210;
  width: calc(100% - 40px);
  max-width: 480px;
  padding: 24px;
  color: #575757;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, .22);
}

.cookie-consent__title {
  margin: 0 0 12px;
  color: #424242;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
}

.cookie-consent__message,
.cookie-consent__more-info,
.cookie-preferences__item p {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.55;
}

.cookie-consent__privacy-link {
  color: #77ad2e;
  font-weight: 600;
}

.cookie-consent__manage {
  padding: 0;
  color: #424242;
  background: transparent;
  border: 0;
  font-size: 14px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.cookie-consent__actions,
.cookie-preferences__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.cookie-consent__button {
  min-height: 42px;
  padding: 9px 18px;
  background: #fff;
  border: 1px solid currentColor;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  transition: color .2s ease, background-color .2s ease;
  cursor: pointer;
}

.cookie-consent__button--accept {
  color: #77ad2e;
}

.cookie-consent__button--accept:hover,
.cookie-consent__button--accept:focus-visible {
  color: #fff;
  background: #8cc63e;
}

.cookie-consent__button--decline {
  color: #d51e23;
}

.cookie-consent__button--decline:hover,
.cookie-consent__button--decline:focus-visible {
  color: #fff;
  background: #ea2227;
}

.cookie-consent__settings {
  position: fixed;
  left: 16px;
  bottom: 16px;
  z-index: 205;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 9px;
  background: #fff;
  border: 1px solid #8cc63e;
  border-radius: 50%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .15);
  transition: background-color .2s ease, box-shadow .2s ease, transform .2s ease;
  cursor: pointer;
}

.cookie-consent__settings:hover,
.cookie-consent__settings:focus-visible {
  background: #f4faec;
  box-shadow: 0 3px 12px rgba(0, 0, 0, .22);
  transform: translateY(-1px);
}

.cookie-consent__settings-icon {
  display: block;
  width: 32px;
  height: 32px;
}

.cookie-preferences {
  color: #575757;
  border-radius: 6px !important;
}

.cookie-preferences__title {
  color: #424242;
  font-size: 21px !important;
  font-weight: 600 !important;
}

.cookie-preferences__content {
  padding-top: 8px !important;
}

.cookie-preferences__item {
  padding: 12px 0;
  border-top: 1px solid #e6e6e6;
}

.cookie-preferences__item:last-child {
  border-bottom: 1px solid #e6e6e6;
}

.cookie-preferences__item p {
  margin: 8px 0 0 40px;
}

.cookie-preferences__actions {
  padding: 16px 24px 24px !important;
}

.cookie-slide-enter-active,
.cookie-slide-leave-active {
  transition: opacity .25s ease, transform .25s ease;
}

.cookie-slide-enter,
.cookie-slide-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 600px) {
  .cookie-consent__banner {
    left: 12px;
    bottom: 12px;
    width: calc(100% - 24px);
    max-height: calc(100vh - 24px);
    padding: 18px;
    overflow-y: auto;
  }

  .cookie-consent__actions,
  .cookie-preferences__actions {
    flex-direction: column-reverse;
  }

  .cookie-consent__button {
    width: 100%;
  }

  .cookie-consent__settings {
    left: 12px;
    bottom: 12px;
  }
}
</style>
